package com.happyukrainian.revision.kweb.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.happyukrainian.revision.kweb.config.HUColors
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.functions.min
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.aspectRatio
import com.varabyte.kobweb.compose.ui.modifiers.background
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.css.vw

enum class FlashCardConfiguration {
    Question,
    Answer,
}

enum class FlashCardTextLength {
    Short,
    Medium,
    Long,
    ExtraLong,
}

private val flowersQuestion =
    listOf("/images/flowers_one.svg", "/images/flowers_three.svg", "/images/flowers_five.svg")
private val flowersAnswer =
    listOf("/images/flowers_two.svg", "/images/flowers_four.svg", "/images/flowers_six.svg")

private val flowerHeightCompact =
    mapOf(
        FlashCardTextLength.Short to 40,
        FlashCardTextLength.Medium to 35,
        FlashCardTextLength.Long to 30,
        FlashCardTextLength.ExtraLong to 20,
    )

private val flowerHeight =
    mapOf(
        FlashCardTextLength.Short to 60,
        FlashCardTextLength.Medium to 50,
        FlashCardTextLength.Long to 40,
        FlashCardTextLength.ExtraLong to 30,
    )

private val textBoxWidth =
    mapOf(
        FlashCardTextLength.Short to 75,
        FlashCardTextLength.Medium to 80,
        FlashCardTextLength.Long to 90,
        FlashCardTextLength.ExtraLong to 95,
    )

@Composable
fun FlippableFlashCard(
    text: String,
    compact: Boolean,
    onTap: () -> Unit,
    modifier: Modifier = Modifier,
    configuration: FlashCardConfiguration = FlashCardConfiguration.Question,
) {
    val textLength by remember {
        derivedStateOf {
            when (text.length) {
                in 0..50 -> FlashCardTextLength.Short
                in 50..100 -> FlashCardTextLength.Medium
                in 100..200 -> FlashCardTextLength.Long
                else -> FlashCardTextLength.ExtraLong
            }
        }
    }

    val decoration by remember {
        derivedStateOf {
            if (configuration == FlashCardConfiguration.Question) {
                flowersQuestion[text.length.mod(flowersQuestion.size)]
            } else {
                flowersAnswer[text.length.mod(flowersAnswer.size)]
            }
        }
    }

    val textAlign by remember {
        derivedStateOf {
            if (configuration == FlashCardConfiguration.Question) {
                TextAlign.End
            } else {
                TextAlign.Start
            }
        }
    }

    val cardBackground = if (configuration == FlashCardConfiguration.Question) HUColors.questionBackground else HUColors.answerBackground

    Box(
        modifier =
            modifier
                .onClick { onTap() }
                .background(cardBackground)
                .boxShadow(offsetX = 2.px, offsetY = 4.px, 7.2.px, 0.px, HUColors.dropshadowColor)
                .width(100.percent)
                .aspectRatio(if (compact) 1f else 1.5f)
                .borderRadius(10.px),
    ) {
        Image(
            src = decoration,
            modifier =
                Modifier
                    .align(if (configuration == FlashCardConfiguration.Question) Alignment.BottomStart else Alignment.TopEnd)
                    .height(
                        if (compact) {
                            flowerHeightCompact[textLength]!!.percent
                        } else {
                            flowerHeight[textLength]!!.percent
                        },
                    ).opacity(0.65f),
        )

        Box(
            modifier =
                Modifier
                    .width(textBoxWidth[textLength]!!.percent)
                    .height(50.percent)
                    .align(Alignment.Center),
            contentAlignment = Alignment.CenterEnd,
        ) {
            SpanText(
                text = text,
                modifier =
                    Modifier
                        .textAlign(textAlign)
                        .fontSize(
                            if (compact) {
                                compactFontSize(textLength)
                            } else {
                                baseFontSize(textLength)
                            },
                        ).color(HUColors.cardTextColor),
            )
        }
    }
}

fun baseFontSize(textLength: FlashCardTextLength) =
    when (textLength) {
        FlashCardTextLength.Short -> min(4.vw, 4.vh)
        FlashCardTextLength.Medium -> min(3.8.vw, 3.8.vh)
        FlashCardTextLength.Long -> min(3.4.vw, 3.4.vh)
        FlashCardTextLength.ExtraLong -> min(3.vw, 3.vh)
    }

fun compactFontSize(textLength: FlashCardTextLength) =
    when (textLength) {
        FlashCardTextLength.Short -> min(7.5.vw, 7.5.vh)
        FlashCardTextLength.Medium -> min(6.2.vw, 6.2.vh)
        FlashCardTextLength.Long -> min(5.6.vw, 5.6.vh)
        FlashCardTextLength.ExtraLong -> min(5.vw, 5.vh)
    }
